import React, { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import PageBanner from '../components/PageBanner';

const SECTIONS = [
  {
    id: 'psychotherapy',
    label: 'Clinical Care',
    title: 'Psychotherapy, Counselling & Assessment',
    lead:
      'Evidence-based psychological care led by Prof (Dr) Manju Agrawal and our team of certified practitioners. In person at our Lucknow centre or online.',
    services: [
      {
        image: '/assets/services/service-counselling.webp',
        title: 'Counselling, DBT & CBT',
        description:
          'Evidence-based talk therapy for anxiety, depression, stress, grief, trauma and relationship issues. Sessions with certified psychologists in person or online.',
      },
      {
        image: '/assets/services/service-psychometric.webp',
        title: 'Psychometric Testing',
        description:
          'Professional personality assessments, aptitude and intelligence testing, and behavioural analysis — used standalone or alongside therapy.',
      },
      {
        image: '/assets/services/service-sexuality.webp',
        title: 'Sexuality, Marital & Age-related Issues',
        description:
          'Relationship counselling, intimacy and trust building, and support across major life transitions — for individuals and couples.',
      },
    ],
  },
  {
    id: 'hypnotherapy',
    label: 'Beyond Talk Therapy',
    title: 'Hypnotherapy & NLP',
    lead:
      'Guided clinical hypnotherapy and healing & energy work for clients who want to work at a deeper, non-cognitive level. Undertaken standalone or alongside clinical therapy.',
    services: [
      {
        image: '/assets/services/service-hypnotherapy.webp',
        title: 'Hypnotherapy & NLP',
        description:
          'Guided clinical hypnotherapy that reaches the subconscious patterns talk therapy cannot. Effective for phobias, addictions, overthinking, sleep issues and psychosomatic conditions.',
      },
      {
        image: '/assets/services/service-regression.webp',
        title: 'Past Life Regression',
        description:
          'Deep subconscious exploration to resolve karmic patterns and emotional blockages that may not surface through conventional therapy.',
      },
      {
        image: '/assets/services/service-chakra.webp',
        title: 'Chakra Healing & Energy Balancing',
        description:
          'Restore balance across the seven chakras, clear energy stagnation and support emotional, mental and physical well-being through guided energy work.',
      },
      {
        image: '/assets/services/service-womb.webp',
        title: 'Womb Healing',
        description:
          'A focused practice for feminine energy restoration and emotional release of stored grief, trauma or generational patterns.',
      },
      {
        image: '/assets/services/service-psychic.webp',
        title: 'Psychic Surgery',
        description:
          'Non-invasive energy healing aimed at releasing deep-rooted trauma and spiritual blockages, performed in a structured therapeutic setting.',
      },
      {
        image: '/assets/services/service-sexuality.webp',
        title: 'Sexuality, Marital & Age-related Issues',
        description:
          'Relationship counselling, intimacy and trust building, and support across major life transitions — for individuals and couples.',
      },
    ],
  },
  {
    id: 'coaching',
    label: 'Direction & Performance',
    title: 'Coaching & Career Counselling',
    lead:
      'Practical, goal-oriented sessions for students, professionals and executives. Build confidence, clarity and decision-making for what comes next.',
    services: [
      {
        image: '/assets/services/service-coaching.webp',
        title: 'Mind Coaching',
        description:
          'Focused coaching for students and young professionals to build confidence, mental performance and decision-making for academic and career goals.',
      },
      {
        image: '/assets/services/service-career.webp',
        title: 'Career Counselling',
        description:
          'Career-path discovery, aptitude assessment and goal-setting for students and mid-career professionals navigating a transition.',
      },
      {
        image: '/assets/services/service-executive.webp',
        title: 'Executive Coaching',
        description:
          'Leadership development, strategic thinking and work–life balance support for founders, senior managers and high-performing professionals.',
      },
    ],
  },
];

const SectionGrid = ({ services }) => (
  <div className="services-page__grid">
    {services.map((s) => (
      <article key={s.title} className="services-page__card">
        <div className="services-page__card-image">
          <img src={s.image} alt={s.title} loading="lazy" />
        </div>
        <div className="services-page__card-body">
          <h3 className="services-page__card-title">{s.title}</h3>
          <p className="services-page__card-desc">{s.description}</p>
        </div>
      </article>
    ))}
  </div>
);

function ServicesPage() {
  const location = useLocation();

  useEffect(() => {
    if (!location.hash) return;
    const id = location.hash.replace('#', '');
    const t = setTimeout(() => {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 80);
    return () => clearTimeout(t);
  }, [location.pathname, location.hash]);

  return (
    <>
      <PageBanner title="Our Services" />

      <section className="services-page">
        <div className="container">
          {SECTIONS.map((section, idx) => (
            <React.Fragment key={section.id}>
              <header
                id={section.id}
                className={`services-page__intro${idx > 0 ? ' services-page__intro--spaced' : ''}`}
              >
                <p className="section-label">{section.label}</p>
                <h2 className="section-title">{section.title}</h2>
                <p className="services-page__lead">{section.lead}</p>
              </header>
              <SectionGrid services={section.services} />
            </React.Fragment>
          ))}

          <header
            id="training"
            className="services-page__intro services-page__intro--spaced"
          >
            <p className="section-label">Train With Us</p>
            <h2 className="section-title">Hypnotherapy practitioner training</h2>
            <p className="services-page__lead">
              CHI-USA certified practitioner programs in clinical hypnotherapy,
              counselling skills and allied modalities. For aspiring therapists and coaches.
            </p>
          </header>

          <div className="services-page__training">
            <div className="services-page__training-image">
              <img src="/assets/training/training-chi.webp" alt="CHI-USA certified training" loading="lazy" />
            </div>
            <div className="services-page__training-body">
              <h3 className="services-page__training-title">
                CHI-USA Certification Track
              </h3>
              <p className="services-page__training-desc">
                Comprehensive practitioner training taught by Prof (Dr) Manju Agrawal — combining
                clinical hypnotherapy, NLP and counselling skills, with supervised practice and
                certification on completion.
              </p>
              <Link to="/courses" className="btn-primary services-page__training-cta">
                Explore Training Programs
              </Link>
            </div>
          </div>

          <div className="services-page__cta">
            <Link to="/contact" className="btn-primary">
              Book a First Session
            </Link>
            <a
              href="https://wa.me/919140395816"
              target="_blank"
              rel="noopener noreferrer"
              className="services-page__whatsapp"
            >
              or message us on WhatsApp
            </a>
          </div>
        </div>
      </section>

      <style>{`
        .services-page {
          padding: clamp(48px, 7vw, 84px) 0 clamp(56px, 8vw, 96px);
          background: var(--bg-white);
        }
        .services-page__intro {
          text-align: center;
          margin-bottom: 44px;
          scroll-margin-top: 96px;
        }
        .services-page__intro--spaced {
          margin-top: 76px;
          padding-top: 56px;
          border-top: 1px solid var(--border-light);
        }
        .services-page__lead {
          font-family: var(--font-body);
          font-size: 16px;
          color: var(--text-body);
          line-height: 1.7;
          max-width: 680px;
          margin: 12px auto 0;
        }
        .services-page__grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(290px, 1fr));
          gap: 24px;
        }
        .services-page__card {
          background: #fff;
          border: 1px solid var(--border-light);
          border-radius: var(--radius-lg);
          overflow: hidden;
          display: flex;
          flex-direction: column;
          transition: transform 0.3s ease, box-shadow 0.3s ease, border-color 0.3s ease;
        }
        .services-page__card:hover {
          transform: translateY(-4px);
          box-shadow: 0 14px 36px rgba(0,0,0,0.08);
          border-color: var(--accent-light);
        }
        .services-page__card-image {
          width: 100%;
          height: 200px;
          overflow: hidden;
          background: var(--bg-light);
        }
        .services-page__card-image img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          transition: transform 0.5s ease;
        }
        .services-page__card:hover .services-page__card-image img {
          transform: scale(1.04);
        }
        .services-page__card-body {
          padding: 22px 22px 26px;
        }
        .services-page__card-title {
          font-family: var(--font-heading);
          font-size: 20px;
          font-weight: 600;
          color: var(--text-heading);
          margin: 0 0 10px 0;
          line-height: 1.3;
          letter-spacing: -0.01em;
        }
        .services-page__card-desc {
          font-family: var(--font-body);
          font-size: 14.5px;
          color: var(--text-body);
          line-height: 1.65;
          margin: 0;
        }
        .services-page__training {
          display: grid;
          grid-template-columns: 1fr 1.1fr;
          gap: 32px;
          align-items: center;
          background: var(--bg-section);
          border: 1px solid var(--border-light);
          border-radius: var(--radius-lg);
          padding: 24px;
        }
        .services-page__training-image {
          width: 100%;
          height: 100%;
          min-height: 260px;
          overflow: hidden;
          border-radius: calc(var(--radius-lg) - 8px);
          background: var(--bg-light);
        }
        .services-page__training-image img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }
        .services-page__training-body {
          padding: 8px 12px;
        }
        .services-page__training-title {
          font-family: var(--font-heading);
          font-size: 24px;
          font-weight: 600;
          color: var(--text-heading);
          margin: 0 0 14px 0;
          line-height: 1.25;
          letter-spacing: -0.01em;
        }
        .services-page__training-desc {
          font-family: var(--font-body);
          font-size: 15px;
          color: var(--text-body);
          line-height: 1.7;
          margin: 0 0 22px 0;
        }
        .services-page__training-cta {
          display: inline-block;
        }
        .services-page__cta {
          margin-top: 64px;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 14px;
        }
        .services-page__whatsapp {
          font-family: var(--font-body);
          font-size: 14px;
          color: var(--text-light);
          text-decoration: none;
          transition: color 0.2s ease;
        }
        .services-page__whatsapp:hover {
          color: var(--accent);
        }

        @media (max-width: 768px) {
          .services-page__training {
            grid-template-columns: 1fr;
            gap: 20px;
          }
          .services-page__training-image {
            min-height: 220px;
          }
        }

        @media (max-width: 600px) {
          .services-page__intro--spaced {
            margin-top: 56px;
            padding-top: 40px;
          }
          .services-page__card-image {
            height: 180px;
          }
        }
      `}</style>
    </>
  );
}

export default ServicesPage;
